import React, { useEffect, useRef, useState, useMemo } from "react";
import { useNavigate } from 'react-router-dom';
import { inject, observer } from 'mobx-react';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowDown, faArrowUp, faCog, faPencilAlt, faSpinner, faWindowClose } from "@fortawesome/free-solid-svg-icons";
import Modal from '../../components/modal';
import ImportResultModal from '../../components/import-result-modal';
import Switch from "react-switch";
import Select from 'react-select';
import FilterInputField from "../../components/filter-input-field";
import PaginationControls from "../../components/pagination-controls";
import { fetch } from '../../utils/api-wrapper';
import { downloadUserImportTemplateCsv } from '../../utils/user-import-csv-template';
import { downloadRowsAsXlsx } from '../../utils/excel-export';
import { cachedFetch } from '../../utils/cached-api-wrapper';
import Utils from "../../utils/utils";
import axios from "axios";
import AccessControl from "../../utils/access_control";
import { imputationDocumentId } from '../../pages/review-expense/utils/contract-expense-json';
import { UserFormFields } from './admin-user-form-fields';

const userImportSummarySpec = [
    { key: 'users_created', labelKey: 'users_import_summary_created' },
    { key: 'users_updated', labelKey: 'users_import_summary_updated' },
    { key: 'rows_failed', labelKey: 'users_import_summary_failed' },
];

const objectIdValue = (value) => imputationDocumentId({ _id: value }) || value || "";

const namesList = (items = []) => (Array.isArray(items) ? items : [])
    .map((item) => item?.name || item?.label || item?.names?.[0] || item?.email || item)
    .filter(Boolean)
    .join("; ");

const userName = (user = {}) => [user.first_name, user.last_name].filter(Boolean).join(" ").trim();

export const adminUserExportRow = (user = {}, allUsers = []) => {
    const userById = new Map((allUsers ?? []).map((item) => [item.id_user || item._id?.$oid, item]));
    const formatUserRef = (userRef) => {
        const id = userRef?.id_user || userRef;
        const found = userById.get(id) || userRef || {};
        return userName(found) || found.email || id || "";
    };
    const timekeepers = (user.timekeepers ?? [])
        .map((timekeeper) => {
            const base = timekeeper?.name || timekeeper?.index || "";
            return timekeeper?.is_default ? `${base} (default)` : base;
        })
        .filter(Boolean)
        .join("; ");

    return {
        id: objectIdValue(user._id),
        id_client: objectIdValue(user.id_client),
        username: user.username ?? "",
        email: user.email ?? "",
        first_name: user.first_name ?? "",
        last_name: user.last_name ?? "",
        role: user.role ?? "",
        active: user.active !== false,
        supplier_id: user.supplier?.supplier_id ?? objectIdValue(user.supplier?._id),
        supplier_names: namesList(user.supplier?.names ?? []),
        card_ids: namesList(user.cards ?? []),
        home_office_id: objectIdValue(user.home_office_id),
        rank_id: user.rank?.id ?? "",
        rank_description: user.rank?.description ?? "",
        adjoints: (user.adjoints ?? []).map(formatUserRef).filter(Boolean).join("; "),
        timekeepers,
        email_notifications_enabled: !user.is_email_notification_disabled,
        vacation_mode_enabled: Boolean(user.vacation_mode?.enabled),
        vacation_mode_start_date: user.vacation_mode?.start_date ?? "",
        vacation_mode_end_date: user.vacation_mode?.end_date ?? "",
        vacation_mode_delegate_approver: formatUserRef(user.vacation_mode?.delegate_approver),
        self_approval_substitute_approver: formatUserRef(user.self_approval_substitute_approver),
    };
};

const AdminUsers = ({ data }) => {
    const navigate = useNavigate();
    const cancelToken = useMemo(() => axios.CancelToken.source(), []);
    const [errorMessage, setErrorMessage] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [userImportResult, setUserImportResult] = useState(null);
    const [loading, setLoading] = useState(true);
    const [client, setClient] = useState(null);
    const [clients, setClients] = useState(null);
    const [users, setUsers] = useState([]);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalRowCount, setTotalRowCount] = useState(0);
    const [filterValue, setFilterValue] = useState("");
    const [suppliersFilterValue, setSuppliersFilterValue] = useState("");
    const [sorting, setSorting] = useState([{ "id": "email", "desc": false }]);
    const [newUser, setNewUser] = useState({
        email: "",
        first_name: "",
        last_name: "",
        role: "Admin",
        supplier: null,
        card_number: "",
        home_office_id: null,
    });
    const [suppliers, setSuppliers] = useState([]);
    const [allUsers, setAllUsers] = useState([]);
    const [roles, setRoles] = useState([]);
    
    const [updatedUser, setUpdatedUser] = useState({
        _id: null,
        email: "",
        first_name: "",
        last_name: "",
        role: "",
        card_number: "",
        card_type: ""
    });
    const [originalUser, setOriginalUser] = useState({
        _id: null,
        email: "",
        first_name: "",
        last_name: "",
        role: "",
        card_number: "",
        card_type: ""
    });
    const [showDeleteUserModal, setShowDeleteUserModal] = useState(false);
    const [showResetPasswordConfirm, setShowResetPasswordConfirm] = useState(false);
    const [showUnsavedChangesPasswordReset, setShowUnsavedChangesPasswordReset] = useState(false);
    const [showConfirmEmailChange, setShowConfirmEmailChange] = useState(false);
    const [userToResetPassword, setUserToResetPassword] = useState(null);
    const [showNewUserModal, setShowNewUserModal] = useState(false);
    const [showUpdateUserModal, setShowUpdateUserModal] = useState(false);
    const [userDeleteSelection, setUserDeleteSelection] = useState(null);
    const [importFile, setImportFile] = useState(null);
    const [showImportModal, setShowImportModal] = useState(false);
    const [exporting, setExporting] = useState(false);
    const timekeeperQueryCache = useRef({});
    const [timekeeperCache, setTimekeeperCache] = useState({});
    const [offices, setOffices] = useState([]);
    const [rankOptions, setRankOptions] = useState([]);

    const hasAderantIntegration = Utils.hasAderantIntegration({ config: client?.config });
    // The client being edited, not the session: an admin can manage several.
    const isEliteClient = Utils.isExportFormat({ config: client?.config }, 'elite');
    const canFetchRankOptions = hasAderantIntegration && Utils.hasAderantIntegration(data);

    useEffect(() => {
        setLoading(true);
        refreshClients();
        return () => {
            cancelToken.cancel();
        }
    }, []);

    useEffect(() => {
        if (client) {
            setPage(1);
            refreshUsers(client);
            refreshAllUsers(client)
            loadRoles(client)
            refreshOffices(client)
        }
    }, [client, pageSize, sorting, filterValue]);

    useEffect(() => {
        if (client) {
            refreshUsers(client)
            refreshAllUsers(client)
        }
    }, [page]);

    useEffect(() => {
        refreshSuppliers();
    }, [suppliersFilterValue])

    const loadRankOptions = () => {
        if (!canFetchRankOptions) {
            setRankOptions([]);
            return;
        }
        fetch("/client/options/?field_name=Rank", "GET", {}, (response) => {
            setRankOptions(Utils.mapRankOptionsFromApi(response?.data));
        });
    };

    useEffect(() => {
        loadRankOptions();
    }, [canFetchRankOptions]);

    useEffect(() => {
        if (!updatedUser?.timekeepers) return;

        const missingIndices = updatedUser.timekeepers
            .map(item => item.index)
            .filter(index => !(index in (timekeeperCache.current || {})));
        if (missingIndices.length > 0) {
            missingIndices.forEach(index => {
                loadTimekeepers(index?.toString());
            });
        }
    }, [updatedUser]);
    
    const loadRoles = (selectedClient = client) => {
        const client_id = selectedClient?._id;

        fetch("/roles/names/"+client_id, "GET", {}, response => {
            const fetchedRoles = response.data;
            setRoles(fetchedRoles);
        });
    }
    const loadTimekeepers = (search_query = "", callback = () => { }) => {
        const val = search_query.trim().replace(/\//g, '');
        const cached = timekeeperQueryCache.current[val];
        if (!cached) {
            cachedFetch(`/timekeepers/search/${encodeURIComponent(val)}`, "GET", {}, (response) => {
                const data = response.data;
                timekeeperQueryCache.current[val] = data;
                // cache fetched timekeepers
                setTimekeeperCache((prev) => {
                    const updated = { ...prev };
                    data.forEach((timekeeper) => {
                        updated[timekeeper.index] = timekeeper;
                    });
                    return updated;
                });

                // return searched timekeepers
                callback(data.map(timekeeperMap))
            })
        } else {
            callback(cached.map(timekeeperMap));
        }
    }


    const timekeeperMap = (timekeeper) => {
        return timekeeper ? {
            value: timekeeper,
            label: timekeeper.name
        }
            :
            null
    }

    const refreshSuppliers = () => {
        const suppliersStart = 0;
        const suppliersPageSize = 10;
        const suppliersFilters = [];
        const suppliersSorting = [{ id: "names", desc: false }];
        if (suppliersFilterValue && suppliersFilterValue.trim() !== "") {
            suppliersFilters.push({ id: "search", value: suppliersFilterValue.trim() });
        }
        const searchData = `?filters=${encodeURIComponent(JSON.stringify(suppliersFilters ?? []))}&sorting=${encodeURIComponent(JSON.stringify(suppliersSorting ?? []))}&start=${encodeURIComponent(suppliersStart)}&page_size=${suppliersPageSize}`;
        fetch(`/suppliers/${searchData}`, "GET", {},

            response => {
                const fetchedSuppliers = response.data.suppliers;
                setSuppliers(fetchedSuppliers);
            });
    }

    const refreshClients = () => {
        fetch("/clients/", "GET", {}, response => {
            const clients = response.data.clients;
            setClients(clients);

            setClient(clients.find(c => c._id === data.id_client)); //Set Client to the user's corresponding client

            setTimekeeperCache({})
            timekeeperQueryCache.current = {}
            loadTimekeepers()
            setLoading(false);
        },
            error => {
                // Error handling
                setErrorMessage(data.t.read("failed_to_fetch_clients"));
                setLoading(false);
            }
        );
    }

    const selectClient = (client) => {
        setClient({ ...client })
        setPage(1)
        setFilterValue("");
        refreshUsers(client);
        refreshAllUsers(client);

        setTimekeeperCache({})
        timekeeperQueryCache.current = {}
        loadTimekeepers()
    }


    const refreshUsers = (selectedClient = client) => {
        let start = (page - 1) * pageSize;
        const filters = [{ id: "id_client", value: selectedClient._id }];

        if (filterValue && filterValue.trim() !== "") {
            filters.push({ id: "search", value: filterValue.trim() });
        }
        const searchData = `?filters=${encodeURIComponent(JSON.stringify(filters ?? []))}&sorting=${encodeURIComponent(JSON.stringify(sorting ?? []))}&start=${encodeURIComponent(start)}&page_size=${pageSize}`;
        fetch(`/users/${searchData}`, "GET", {}, response => {
            let fetchedUsers = response.data.users;
            setUsers([...fetchedUsers]);
            setTotalRowCount(response.data.meta?.totalRowCount);
            setLoading(false);
        });
    }

    const fetchAllUsersForExport = (selectedClient = client) => new Promise((resolve, reject) => {
        if (!selectedClient?._id) {
            resolve([]);
            return;
        }

        const pageSize = 9999;
        const filters = [{ id: "id_client", value: selectedClient._id }];
        const rows = [];

        const fetchPage = (start = 0) => {
            const searchData = `?filters=${encodeURIComponent(JSON.stringify(filters))}&sorting=${encodeURIComponent(JSON.stringify(sorting ?? []))}&start=${encodeURIComponent(start)}&page_size=${pageSize}`;
            fetch(`/users/${searchData}`, "GET", {}, response => {
                const fetchedUsers = response.data.users ?? [];
                rows.push(...fetchedUsers);
                const total = response.data.meta?.totalRowCount ?? rows.length;
                if (rows.length < total && fetchedUsers.length > 0) {
                    fetchPage(start + fetchedUsers.length);
                    return;
                }
                resolve(rows);
            }, reject);
        };

        fetchPage();
    });

    const exportUsersToExcel = async () => {
        setExporting(true);
        try {
            const exportedUsers = await fetchAllUsersForExport(client);
            downloadRowsAsXlsx(
                exportedUsers.map((user) => adminUserExportRow(user, allUsers)),
                `users-${client?.name || client?._id || "selected-client"}.xlsx`,
                "Users"
            );
        } catch (error) {
            setErrorMessage(data.t.read("failed_to_export_to_excel"));
        } finally {
            setExporting(false);
        }
    };

    const loadCards = (search_query = "", callback = () => {
    }) => {
        // fetch cards from the api search with field label, card_masked_number
        const filters = [{ id: "id_client", value: client?._id ?? data.id_client }];
        if (search_query.length > 0) {
            filters.push({ id: "search", value: search_query });
        }
        const cardSorting = [{ id: "label", desc: false }];
        const cardPageSize = 20;
        const searchData = `?filters=${encodeURIComponent(JSON.stringify(filters ?? []))}&sorting=${encodeURIComponent(JSON.stringify(cardSorting ?? []))}&page_size=${cardPageSize}`;
        fetch(`/c_card/${searchData}`, "GET", {},
            response => {
                callback(response?.data?.c_cards?.map(c => ({ label: c.label, value: c._id?.$oid })) ?? []);
            }, null, false, cancelToken.token);

    }

    const refreshAllUsers = (selectedClient = client) => {
        const client_id = selectedClient?._id;
        fetch(`/client/${client_id}/usersDetail`, "GET", {}, response => {
            let fetchedUsers = response.data;
            setAllUsers([...fetchedUsers]);
            setLoading(false);
        });
    }

    const refreshOffices = (selectedClient = client) => {
        const filters = [{ id: "id_client", value: selectedClient._id }];
        const searchData = `?filters=${encodeURIComponent(JSON.stringify(filters))}&sorting=${encodeURIComponent(JSON.stringify([{ id: "name", desc: false }]))}&start=0&page_size=500`;
        fetch(`/offices/${searchData}`, "GET", {},
            response => setOffices(response.data.offices)
        );
    };

    // Form submission handler
    const updateUserSubmit = (e) => {
        e.preventDefault();
        if (updatedUser.email !== originalUser.email) {
            setShowConfirmEmailChange(true);
            return;
        }
        commitUpdateUser();
    };

    const commitUpdateUser = () => {
        setShowConfirmEmailChange(false);
        setShowUpdateUserModal(false);
        updateUser();
    };

    const prepareUserSavePayload = (user) => {
        const payload = { ...user };
        if (payload.vacation_mode != null) {
            payload.vacation_mode = normalizeVacationMode(payload.vacation_mode);
        }
        if (payload.self_approval_substitute_approver != null) {
            payload.self_approval_substitute_approver = normalizeSelfApprovalSubstituteApprover(
                payload.self_approval_substitute_approver
            );
        }
        if (!hasAderantIntegration) {
            delete payload.rank;
        }
        return payload;
    };

    const updateUser = (userId = updatedUser._id?.$oid, user = updatedUser) => {
        const updatedUserWithoutId = prepareUserSavePayload({ ...user });
        delete updatedUserWithoutId._id;
        const formData = new FormData();
        formData.append('user', JSON.stringify(updatedUserWithoutId));
        fetch(`/users/${userId}`, "PUT", formData,
            () => {
                refreshUsers(client);
                setSuccessMessage(data.t.read("user_updated_successfully"))
            },
            () => {
                setErrorMessage(data.t.read("failed_to_update_user"))
                setLoading(false);
            }
        );
    }

    //function to check if a user has been updated
    const isUserUpdated = (updatedUser, originalUser) => {
        return (
            updatedUser.email !== originalUser.email ||
            updatedUser.first_name !== originalUser.first_name ||
            updatedUser.last_name !== originalUser.last_name ||
            updatedUser.active !== originalUser.active ||
            updatedUser.role !== originalUser.role ||
            updatedUser.supplier !== originalUser.supplier ||
            updatedUser.cards !== originalUser.cards ||
            JSON.stringify(updatedUser.adjoints ?? [])
                !== JSON.stringify(originalUser.adjoints ?? []) ||
            JSON.stringify(updatedUser.timekeepers ?? null) !== JSON.stringify(originalUser.timekeepers ?? null) ||
            updatedUser.is_email_notification_disabled !== originalUser.is_email_notification_disabled ||
            JSON.stringify(updatedUser.vacation_mode ?? null) !== JSON.stringify(originalUser.vacation_mode ?? null) ||
            JSON.stringify(updatedUser.self_approval_substitute_approver ?? null)
                !== JSON.stringify(originalUser.self_approval_substitute_approver ?? null) ||
            (updatedUser.home_office_id ?? null) !== (originalUser.home_office_id ?? null) ||
            (updatedUser.rank?.id ?? null) !== (originalUser.rank?.id ?? null)
        );
    };

    const normalizeVacationMode = (vacationMode = null) => ({
        enabled: Boolean(vacationMode?.enabled),
        start_date: vacationMode?.start_date ?? "",
        end_date: vacationMode?.end_date ?? "",
        delegate_approver: vacationMode?.delegate_approver ?? null,
        assigned_by: vacationMode?.assigned_by ?? null,
    });

    const normalizeSelfApprovalSubstituteApprover = (approver = null) => {
        if (!approver?.id_user) return null;
        return {
            id_user: approver.id_user,
            username: approver.username,
            first_name: approver.first_name ?? "",
            last_name: approver.last_name ?? "",
            email: approver.email ?? "",
            role: approver.role,
        };
    };

    const normalizeUserHomeOfficeId = (raw) => {
        if (raw == null || raw === '') return null;
        const id = imputationDocumentId({ _id: raw });
        return id || null;
    };

    const isVacationModeInvalid = (user) => (
        user.vacation_mode?.enabled &&
        (
            !user.vacation_mode?.start_date ||
            !user.vacation_mode?.end_date ||
            !user.vacation_mode?.delegate_approver ||
            user.vacation_mode?.end_date < user.vacation_mode?.start_date
        )
    );

    const buildVacationDelegateOptions = (excludeUserId = null) => allUsers
        .filter((user) =>
            (excludeUserId == null || user?.id_user !== excludeUserId) &&
            user?.active !== false &&
            ["Approver", "Admin"].includes(user?.role)
        )
        .map((user) => ({
            value: user.id_user,
            label: `${user.first_name || ""} ${user.last_name || ""}`.trim(),
            raw: {
                id_user: user.id_user,
                username: user.username,
                first_name: user.first_name,
                last_name: user.last_name,
                email: user.email,
                role: user.role,
            },
        }));

    const openUpdateUserModal = (selectedUser) => {
        loadRankOptions();
        setUpdatedUser({
            ...selectedUser,
            _id: selectedUser._id || null,
            email: selectedUser.email || "",
            first_name: selectedUser.first_name || "",
            last_name: selectedUser.last_name || "",
            role: selectedUser.role || "",
            vacation_mode: normalizeVacationMode(selectedUser.vacation_mode),
            self_approval_substitute_approver: normalizeSelfApprovalSubstituteApprover(
                selectedUser.self_approval_substitute_approver
            ),
            home_office_id: normalizeUserHomeOfficeId(selectedUser.home_office_id),
        });
        setOriginalUser({
            ...selectedUser,
            _id: selectedUser._id || null,
            email: selectedUser.email || "",
            first_name: selectedUser.first_name || "",
            last_name: selectedUser.last_name || "",
            role: selectedUser.role || "",
            vacation_mode: normalizeVacationMode(selectedUser.vacation_mode),
            self_approval_substitute_approver: normalizeSelfApprovalSubstituteApprover(
                selectedUser.self_approval_substitute_approver
            ),
            home_office_id: normalizeUserHomeOfficeId(selectedUser.home_office_id),
        })
        setShowUpdateUserModal(true)
    }

    const cancelUpdateUserModal = () => {
        setShowUpdateUserModal(false);
        setUpdatedUser({})
        setOriginalUser({})
    }

    const deleteUsers = () => {

        fetch("/users/", "DELETE", { "user_ids": [userDeleteSelection._id?.$oid] },
            () => {
                refreshUsers();
                setSuccessMessage(data.t.read("user_deleted_successfully"))
                setUserDeleteSelection(null)
            },
            (error) => {
                setErrorMessage(data.t.read("failed_to_delete"))
                setLoading(false);
            }
        );
    }

    const confirmDeleteUser = () => {
        deleteUsers()
        setShowDeleteUserModal(false);
    };

    const openDeleteUserModal = () => {
        setShowDeleteUserModal(true);
    }

    const cancelDeleteOfficeModal = () => {
        setShowDeleteUserModal(false);
        setUserDeleteSelection(null)
    }

    const generatePassword = (user) => {
        setUserToResetPassword(user);
        setShowResetPasswordConfirm(true);
    };

    const confirmResetPassword = () => {
        setUserToResetPassword({ ...userToResetPassword, is_generating: true })

        fetch("/users/generate_password",
            "PUT",
            { _id: userToResetPassword._id?.$oid },
            (response) => {
                setSuccessMessage(
                    `${userToResetPassword.first_name} ${userToResetPassword.last_name}\n` + data.t.read("password_reset_successful")
                );
                setShowResetPasswordConfirm(false);
                setUserToResetPassword(null);
            },
            (response) => {
                setErrorMessage(
                    `${userToResetPassword.first_name} ${userToResetPassword.last_name}\n` + data.t.read("password_reset_failed")
                );
                setShowResetPasswordConfirm(false);
                setUserToResetPassword(null);
            }
        );
    };

    const cancelResetPassword = () => {
        setShowResetPasswordConfirm(false);
        setUserToResetPassword(null);
    };

    const requestPasswordResetFromEditModal = (user) => {
        if (isUserUpdated(updatedUser, originalUser)) {
            setShowUnsavedChangesPasswordReset(true);
            return;
        }
        generatePassword(user);
    };

    const confirmPasswordResetWithUnsavedChanges = () => {
        setShowUnsavedChangesPasswordReset(false);
        generatePassword(originalUser);
    };


    const createUser = (user = newUser) => {
        const payload = prepareUserSavePayload(user);
        const formData = new FormData();
        formData.append('user', JSON.stringify(payload));

        fetch("/users/", "POST", formData,
            () => {
                setSuccessMessage(data.t.read("user_created_successfully"));
                refreshUsers(client);
            },
            (error) => {
                setErrorMessage(error?.response?.data?.message || data.t.read("internal_saving_error"));
                setLoading(false);
            }
        );
    };

    const newUserSubmit = (e) => {
        e.preventDefault();
        setShowNewUserModal(false)
        createUser();
    };

    const openNewUserModal = () => {
        loadRankOptions();
        setShowNewUserModal(true);
        setNewUser({
            id_client: client._id,
            email: "",
            first_name: "",
            last_name: "",
            role: newUserAvailableRoles()[0] ?? "Admin",
            card_number: "",
            card_type: "",
            is_email_notification_disabled: false,
            self_approval_substitute_approver: null,
            home_office_id: null,
            rank: null,
            active: true,
        });
    };


    const cancelNewUserModal = () => {
        setShowNewUserModal(false);
        // Reset newUser fields
        setNewUser({
            email: "",
            first_name: "",
            last_name: "",
            role: newUserAvailableRoles()[0] ?? "Admin",
            card_number: "",
            card_type: "",
            is_email_notification_disabled: false,
            self_approval_substitute_approver: null,
            home_office_id: null,
            rank: null,
            active: true,
        });
    };

    const prepareUserImport = (files) => {
        if (files.length > 0) {
            setImportFile(files[0]);
        }
    };

    const processUserImport = () => {
        if (importFile != null) {
            setErrorMessage(null);
            setSuccessMessage(null);
            setUserImportResult(null);
            const formData = new FormData();
            formData.append("csv", importFile);

            fetch("/users/import", "POST", formData,
                (response) => {
                    setShowImportModal(false);
                    setImportFile(null);
                    const body = response?.data ?? {};
                    if (body.status) {
                        // New structured report (XPEN-3145-007) — success or partial_success, both 2xx.
                        setUserImportResult(body);
                    } else {
                        const { success, failed, errors } = body;
                        const countMsg = `${success} ${data.t.read("import_users_result_imported")}, ${failed} ${data.t.read("import_users_result_failed")}`;
                        if (errors?.length > 0) {
                            setErrorMessage(`${countMsg}\n\n${errors.join("\n")}`);
                        } else {
                            setSuccessMessage(countMsg);
                        }
                    }
                    refreshUsers(client);
                },
                (e) => {
                    setShowImportModal(false);
                    setImportFile(null);
                    const body = e?.response?.data ?? {};
                    // Gate on body.status alone, not on body.errors/summary/alerts: today's real
                    // 400s from this route ({message, errors: [string]}, no status) would otherwise
                    // be misread as a structured report with zeroed counters. Same lesson as the
                    // client-import screen in XPEN-3289. Every rejection carries status once
                    // XPEN-3145-007 ships, so this is equivalent going forward.
                    if (body.status) {
                        setUserImportResult(body);
                        refreshUsers(client);
                    } else {
                        setErrorMessage(data.t.read("failed_to_import_users"));
                    }
                },
                true
            );
        }
    };

    const newUserAvailableRoles = () => {
        if (client === null) return []
        // let roles = client.config.roles
        let returnRoles = [];

        // if SuperAdmin, only Admin is available
        if (data.role === "SuperAdmin") {
            returnRoles.push("Admin")
        }
        // if Admin, all roles equal or below Admin are available
        else if (data.role === "Admin") {
            returnRoles = roles.filter(role => role !== "SuperAdmin")
        }
        return returnRoles;
    }

    const sortTable = (name) => {
        const sorter = sorting.find(s => s.id === name)
        if (sorter === undefined)
            return setSorting([{ "id": name, "desc": true }])

        if (sorter.desc)
            return setSorting([{ "id": name, "desc": false }])
        else
            return setSorting([])
    }

    const handlePageChange = (newPage) => {
        setPage(newPage);
    };

    const handleRowsPerPageChange = (rows) => {
        setPage(1)
        setPageSize(rows)
    }

    //pagination logic
    const indexOfFirstItem = (page - 1) * pageSize;
    const indexOfLastItem = indexOfFirstItem + users.length - 1;
    const totalPages = Math.ceil(totalRowCount / pageSize);
    const emptyRowsCount = Math.max(0, pageSize - users.length);
    const emptyRows = Array(emptyRowsCount).fill({});

    const styleSelect = {
        ...Utils.styleSelect,
        control: (baseStyles, state) => ({
            ...baseStyles,
            minHeight: 38,
            border: state.isFocused ? '1px solid #F39800' : '1px solid #ced4da',
            boxShadow: state.isFocused ? '0px 0px 10px #F39800' : 'none',
        }),
    };

    const adminUserSelectFieldWrapClass = 'admin-user-select-field-wrap';

    const userMap = (user) => {
        const id = user.id_user || user;
        const user_detail = allUsers.find(u => u.id_user === id) || {};

        return {
            value: id,  // primitive
            label: `${user_detail.first_name} ${user_detail.last_name}`,
        };
    };

    const vacationDelegateOptions = buildVacationDelegateOptions(updatedUser?._id?.$oid);
    const selfApprovalSubstituteOptions = buildVacationDelegateOptions(updatedUser?._id?.$oid);



    return !loading ?
        <div>
            <div>
                <div className='pagination-row'>
                    <FilterInputField filterValue={filterValue} setFilterValue={setFilterValue}
                        placeholder={""}></FilterInputField>
                    <PaginationControls
                        page={page}
                        totalPages={totalPages}
                        indexOfFirstItem={indexOfFirstItem}
                        indexOfLastItem={indexOfLastItem}
                        handlePageChange={handlePageChange}
                        totalItems={totalRowCount}
                        data={data}
                        handleRowsPerPageChange={handleRowsPerPageChange}
                        rowsPerPage={pageSize}
                    ></PaginationControls>
                </div>
                <div className="d-flex justify-content-between align-items-center px-4 py-2">
                    <div className="flex mr-3">
                        <Select
                            name="client"
                            isDisabled={data.role !== "SuperAdmin"}
                            options={clients.map(client => ({ value: client, label: client.name }))}
                            styles={styleSelect}
                            value={client === null ? null : { value: client, label: client.name }}
                            classNamePrefix="select"
                            onChange={(choice) => {
                                selectClient(choice.value);
                            }}
                        />
                    </div>
                    {Utils.userHasRole(data, ["SuperAdmin", "Admin", "Manager"]) && <div className="d-flex">
                        {Utils.userHasRole(data, ["SuperAdmin", "Admin"]) && (
                            <button
                                className='btn btn-outline-secondary btn-sm ml-2'
                                type="button"
                                disabled={client === null}
                                onClick={() => navigate(`/admin/users/scim/${client._id}`, { state: { client } })}
                            >
                                {data.t.read("scim_provisioning_page.entry_button")}
                            </button>
                        )}
                        <button
                            className='btn btn-secondary btn-sm ml-2'
                            type="button"
                            disabled={client === null || exporting}
                            onClick={exportUsersToExcel}
                        >
                            {exporting ? data.t.read("exporting") : data.t.read("export")}
                        </button>
                        <button
                            className='btn btn-secondary btn-sm ml-2'
                            type="button"
                            disabled={client === null}
                            onClick={() => { setImportFile(null); setShowImportModal(true); }}
                        >
                            {data.t.read("import_users")}
                        </button>
                        <button
                            className='btn btn-primary btn-sm ml-2'
                            type="button" disabled={client === null}
                            onClick={openNewUserModal}
                        >
                            {data.t.read("add")}
                        </button>
                    </div>}
                </div>

                <div className="mb-2 card rounded-lg admin-table">
                    <table className='table table-hover font-small'>
                        <thead>
                            <tr>
                                <th className=" pointer col-3" onClick={() => sortTable("email")}>
                                    {data.t.read("rule.email")}
                                    {sorting.find(s => s.id === "email") &&
                                        <FontAwesomeIcon
                                            className={loading ? "infinite-rotate faded ml-2 fa-xs" : "ml-2 fa-xs"}
                                            icon={sorting.find(s => s.id === "email").desc ? faArrowDown : faArrowUp} />}
                                </th>
                                <th className="pointer col-2" onClick={() => sortTable("first_name")}>
                                    {data.t.read("first_name")}
                                    {sorting.find(s => s.id === "first_name") &&
                                        <FontAwesomeIcon
                                            className={loading ? "infinite-rotate faded ml-2 fa-xs" : "ml-2 fa-xs"}
                                            icon={sorting.find(s => s.id === "first_name").desc ? faArrowDown : faArrowUp} />}
                                </th>
                                <th className="pointer col-2" onClick={() => sortTable("last_name")}>
                                    {data.t.read("last_name")}
                                    {sorting.find(s => s.id === "last_name") &&
                                        <FontAwesomeIcon
                                            className={loading ? "infinite-rotate faded ml-2 fa-xs" : "ml-2 fa-xs"}
                                            icon={sorting.find(s => s.id === "last_name").desc ? faArrowDown : faArrowUp} />}
                                </th>
                                <th className="pointer col-1" onClick={() => sortTable("role")}>
                                    {data.t.read("client_table.role")}
                                    {sorting.find(s => s.id === "role") &&
                                        <FontAwesomeIcon
                                            className={loading ? "infinite-rotate faded ml-2 fa-xs" : "ml-2 fa-xs"}
                                            icon={sorting.find(s => s.id === "role").desc ? faArrowDown : faArrowUp} />}
                                </th>
                                <th className="col-1 text-center">
                                    {data.t.read("client.users.active")}

                                </th>
                                <th className="col-2 text-center">
                                    {data.t.read("password")}
                                </th>
                                <th className="col-1 text-center">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {
                                users.map((user) => {
                                    return <tr hidden={user?.is_deleted} key={user._id?.$oid}>
                                        <td className="col-3" onClick={() => (Utils.userCanModify(data, user) || AccessControl.userCanModify(data, user)) && openUpdateUserModal(user)}>
                                            {user.email}
                                        </td>

                                        <td className="col-2" onClick={() =>(Utils.userCanModify(data, user) || AccessControl.userCanModify(data, user)) && openUpdateUserModal(user)}>
                                            {user.first_name}
                                        </td>
                                        <td className="col-2" onClick={() => (Utils.userCanModify(data, user) || AccessControl.userCanModify(data, user)) && openUpdateUserModal(user)}>
                                            {user.last_name}

                                        </td>
                                        <td className="col-1" onClick={() => (Utils.userCanModify(data, user) || AccessControl.userCanModify(data, user)) && openUpdateUserModal(user)}>
                                            {data?.t?.read(`roles.${user.role.toLowerCase()}`) || user.role}
                                        </td>
                                        <td className="col-1">
                                            <div className=" d-flex justify-content-center align-items-center">
                                                <Switch
                                                    className='switch mt-0  '
                                                    checked={user.active}
                                                    disabled={!(Utils.userCanModify(data, user) || AccessControl.userCanModify(data, user))}
                                                    width={70}
                                                    onColor="#F39800"
                                                    onHandleColor="#2693e6"
                                                    boxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)"
                                                    activeBoxShadow="0px 0px 1px 10px rgba(0, 0, 0, 0.2)"
                                                    onChange={(active) => (Utils.userCanModify(data, user) || AccessControl.userCanModify(data, user)) && updateUser(user._id?.$oid, {
                                                        ...user,
                                                        active
                                                    })}
                                                />
                                            </div>
                                        </td>
                                        <td className="col-2 ">
                                            <div className="d-flex justify-content-center align-items-center">
                                                <button
                                                    className="btn btn-primary admin-user-reset-toggle-btn"
                                                    type="button"
                                                    disabled={!(Utils.userCanModify(data, user) || AccessControl.userCanModify(data, user)) | ((user._id === userToResetPassword?._id) && userToResetPassword?.is_generating) | !user._id}
                                                    onClick={() => (Utils.userCanModify(data, user) || AccessControl.userCanModify(data, user)) && generatePassword(user)}
                                                >
                                                    <FontAwesomeIcon
                                                        className={((user._id === userToResetPassword?._id) && userToResetPassword?.is_generating) ? 'infinite-rotate' : 'mr-1'}
                                                        icon={faCog}
                                                    />
                                                    <span>{data.t.read('reset')}</span>
                                                </button>
                                            </div>
                                        </td>

                                        <td className=" col-1 ">
                                            <div className=" d-flex justify-content-center align-items-center">
                                                {(Utils.userCanModify(data, user) || AccessControl.userCanModify(data, user)) && <FontAwesomeIcon className="mx-2 my-auto"
                                                    icon={faPencilAlt}
                                                    onClick={() => openUpdateUserModal(user)}
                                                    type="button" />}

                                                {(Utils.userCanModify(data, user) || AccessControl.userCanModify(data, user)) && <FontAwesomeIcon icon={faWindowClose} type="button"
                                                    onClick={() => {
                                                        setUserDeleteSelection(user);
                                                        openDeleteUserModal()
                                                    }}
                                                    style={{ color: 'red' }} />}
                                            </div>
                                        </td>
                                    </tr>
                                })
                            }
                            {
                                emptyRows.map((_, idx) => (
                                    <tr key={`empty-${idx}`}>
                                        <td colSpan="7">&nbsp;</td>
                                    </tr>
                                ))
                            }
                        </tbody>
                    </table>
                </div>
            </div>
            {showDeleteUserModal && (
                <Modal
                    title={` ${userDeleteSelection?.first_name} ${userDeleteSelection?.last_name}`}
                    okAction={confirmDeleteUser}
                    cancelAction={cancelDeleteOfficeModal}
                    okText={data.t.read("delete")}
                    cancelText={data.t.read("cancel")}>
                    <p className='text-danger'>{data.t.read("confirm_delete_user")}</p>
                </Modal>
            )}
            {
                errorMessage && (
                    <Modal title="Attention" okText={"OK"} okAction={() => setErrorMessage(null)}>
                        <p style={{ whiteSpace: 'pre-line' }} className='text-danger'>{errorMessage}</p>
                    </Modal>)
            }
            {
                successMessage && (
                    <Modal title="Attention" okText={"OK"} okAction={() => setSuccessMessage(null)}>
                        {successMessage}
                    </Modal>

                )
            }
            {userImportResult && (
                <ImportResultModal
                    data={data}
                    result={userImportResult}
                    summarySpec={userImportSummarySpec}
                    onClose={() => setUserImportResult(null)}
                />
            )}
            {showImportModal && (
                <Modal
                    width="50vw"
                    title={data.t.read("import_users")}
                    okText={data.t.read("import")}
                    okAction={processUserImport}
                    cancelText={data.t.read("cancel")}
                    cancelAction={() => setShowImportModal(false)}
                >
                    <div>
                        <div className="card p-2 mb-2">
                            <p className="mb-2">{data.t.read("import_users_help")}</p>
                            <button
                                type="button"
                                className="btn btn-link p-0"
                                onClick={downloadUserImportTemplateCsv}
                            >
                                {data.t.read("supplier_table.download_csv_template")}
                            </button>
                        </div>
                        <div className="mt-2 mb-2">
                            <input
                                className="form-control-file"
                                type="file"
                                accept=".csv"
                                id="import-users-csv-file"
                                onChange={f => prepareUserImport(f.target.files)}
                            />
                        </div>
                    </div>
                </Modal>
            )}
            {showNewUserModal && (
                <Modal
                    title={data.t.read("new_user")}>
                    {/* Form fields for new user */}
                    <form className="admin-users-modal-form" onSubmit={newUserSubmit}>
                        <UserFormFields
                            data={data}
                            user={newUser}
                            setUser={setNewUser}
                            mode="create"
                            offices={offices}
                            availableRoles={newUserAvailableRoles()}
                            adjointOptions={allUsers}
                            suppliers={suppliers}
                            setSuppliersFilterValue={setSuppliersFilterValue}
                            styleSelect={styleSelect}
                            adminUserSelectFieldWrapClass={adminUserSelectFieldWrapClass}
                            userMap={userMap}
                            timekeeperCache={timekeeperCache}
                            loadTimekeepers={loadTimekeepers}
                            timekeeperMap={timekeeperMap}
                            loadCards={loadCards}
                            selfApprovalSubstituteOptions={buildVacationDelegateOptions()}
                            hasAderantIntegration={hasAderantIntegration}
                            isEliteClient={isEliteClient}
                            rankOptions={rankOptions}
                        />

                        <span className="d-flex justify-content-between">
                            <button type="button" className={"btn btn-outline-dark"}
                                onClick={cancelNewUserModal}>
                                {data.t.read("cancel")}
                            </button>
                            <button type="submit" className="btn btn-primary">
                                {data.t.read("save")}
                            </button>
                        </span>
                    </form>
                </Modal>
            )}
            {showUpdateUserModal && (
                <Modal title={originalUser.first_name + " " + originalUser.last_name}>
                    <form className="admin-users-modal-form" onSubmit={updateUserSubmit}>
                        <UserFormFields
                            data={data}
                            user={updatedUser}
                            setUser={setUpdatedUser}
                            mode="edit"
                            offices={offices}
                            availableRoles={newUserAvailableRoles()}
                            adjointOptions={allUsers.filter((u) => u._id?.$oid !== updatedUser._id?.$oid)}
                            suppliers={suppliers}
                            setSuppliersFilterValue={setSuppliersFilterValue}
                            styleSelect={styleSelect}
                            adminUserSelectFieldWrapClass={adminUserSelectFieldWrapClass}
                            userMap={userMap}
                            timekeeperCache={timekeeperCache}
                            loadTimekeepers={loadTimekeepers}
                            timekeeperMap={timekeeperMap}
                            normalizeVacationMode={normalizeVacationMode}
                            vacationDelegateOptions={vacationDelegateOptions}
                            selfApprovalSubstituteOptions={selfApprovalSubstituteOptions}
                            loadCards={loadCards}
                            originalUser={originalUser}
                            onResetPassword={requestPasswordResetFromEditModal}
                            userToResetPassword={userToResetPassword}
                            hasAderantIntegration={hasAderantIntegration}
                            isEliteClient={isEliteClient}
                            rankOptions={rankOptions}
                        />
                        <span className="d-flex justify-content-between">
                            <button type="button" className={"btn btn-outline-dark"}
                                onClick={cancelUpdateUserModal}>
                                {data.t.read("cancel")}
                            </button>
                            <button type="submit" className="btn btn-primary"
                                disabled={
                                    !isUserUpdated(updatedUser, originalUser) ||
                                    isVacationModeInvalid(updatedUser)
                                }>
                                {data.t.read("save")}
                            </button>
                        </span>
                    </form>
                </Modal>
            )}
            {showConfirmEmailChange && (
                <Modal
                    title={data.t.read("attention")}
                    okAction={commitUpdateUser}
                    cancelAction={() => setShowConfirmEmailChange(false)}
                    okText={data.t.read("save")}
                    cancelText={data.t.read("cancel")}
                >
                    <p>{data.t.read("client.users.confirm_email_change") || data.t.read("confirm_user_email_change")}</p>
                    <p className="mb-0">
                        <strong>{originalUser.email}</strong>
                        {' → '}
                        <strong>{updatedUser.email}</strong>
                    </p>
                    <p className="text-muted small mt-2 mb-0">
                        {data.t.read("client.users.confirm_email_change_detail") || data.t.read("confirm_user_email_change_detail")}
                    </p>
                </Modal>
            )}
            {showUnsavedChangesPasswordReset && (
                <Modal
                    title={data.t.read("attention")}
                    okAction={confirmPasswordResetWithUnsavedChanges}
                    cancelAction={() => setShowUnsavedChangesPasswordReset(false)}
                    okText={data.t.read("reset")}
                    cancelText={data.t.read("cancel")}
                >
                    <p>{data.t.read("client.users.confirm_unsaved_changes_password_reset") || data.t.read("confirm_unsaved_changes_password_reset")}</p>
                </Modal>
            )}
            {showResetPasswordConfirm && (
                <Modal
                    title="Confirm Password Reset"
                    okAction={confirmResetPassword}
                    cancelAction={cancelResetPassword}
                    okText="Reset Password"
                    cancelText="Cancel"
                >
                    <p>
                        {data.t.read("confirm_reset_password_for")}{" "}
                        <strong>
                            {userToResetPassword?.first_name} {userToResetPassword?.last_name}
                        </strong>
                        ?
                    </p>
                </Modal>
            )}
        </div>
        :
        <div className="d-flex loading-container w-100">
            <FontAwesomeIcon className="infinite-rotate" icon={faSpinner} />
        </div>

}
export default inject('data')(observer(AdminUsers));
